import { useMemo } from 'react';
import { useTranslations } from 'use-intl';
import { CommentsPanel, type CommentErrorKind } from '../comments/index';
import { feedErrorMessage } from '../feed/feed-error-copy';
import { useErrorMessage } from '../../lib/error-message';
import { useResourceComments } from '@/lib/queries/resources';
import {
  useCreateResourceComment,
  useDeleteResourceComment,
  useUpdateResourceComment,
} from '@/lib/mutations/resources';
import { useAuthedSession } from '@/lib/use-session';
import { EDITOR_BODY_FORMAT } from '../editor';

// Resource flavor of the shared CommentsPanel. Mentions stay off: resource
// comment bodies aren't mention-resolved server-side, so an `@` is literal
// text. Error copy reuses the Feed comments catalog — resource error codes
// aren't feed codes, so unknowns fall through to the per-action fallback.

export function ResourceComments({
  resourceId,
  onCommentAdded,
}: {
  resourceId: string;
  onCommentAdded?: () => void;
}) {
  const t = useTranslations('Feed');
  const describeError = useErrorMessage();
  const commentsQuery = useResourceComments(resourceId);
  const createComment = useCreateResourceComment();
  const updateComment = useUpdateResourceComment();
  const deleteComment = useDeleteResourceComment();
  const session = useAuthedSession();

  // Newest-first pages reversed into reading order — same rationale as
  // PostComments: new comments land at the bottom beside the composer and
  // "view earlier" prepends history.
  const items = useMemo(() => {
    const pages = commentsQuery.data?.pages ?? [];
    return pages
      .flatMap((p) => p.items)
      .reverse()
      .map((c) => ({
        id: c.id,
        authorUserId: c.authorUserId,
        authorName: c.authorName,
        authorPhotoUrl: c.authorPhotoUrl,
        body: c.body,
        bodyFormat: c.bodyFormat,
        mentions: [],
        linkPreviews: [],
        createdAt: c.createdAt,
        editedAt: c.editedAt,
      }));
  }, [commentsQuery.data]);

  return (
    <CommentsPanel
      targetId={resourceId}
      items={items}
      isLoading={commentsQuery.isPending}
      loadError={commentsQuery.isError ? commentsQuery.error : null}
      hasEarlier={commentsQuery.hasNextPage === true}
      isFetchingEarlier={commentsQuery.isFetchingNextPage}
      onFetchEarlier={() => void commentsQuery.fetchNextPage()}
      viewerUserId={session?.userId ?? null}
      mentionsEnabled={false}
      onCreate={async (body) => {
        await createComment.mutateAsync({ resourceId, body, bodyFormat: EDITOR_BODY_FORMAT });
      }}
      onUpdate={async (commentId, body) => {
        const updated = await updateComment.mutateAsync({
          commentId,
          resourceId,
          body,
          bodyFormat: EDITOR_BODY_FORMAT,
        });
        return { body: updated.body, bodyFormat: updated.bodyFormat, mentions: [] };
      }}
      onDelete={async (commentId) => {
        await deleteComment.mutateAsync({ commentId, resourceId });
      }}
      formatError={(err, kind: CommentErrorKind) =>
        feedErrorMessage(err, describeError, t, `comments.errors.${kind}`)
      }
      {...(onCommentAdded !== undefined ? { onCommentAdded } : {})}
    />
  );
}
